import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Grid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

interface SimpleDatePickerProps {
  onDateChange: (date: string) => void;
  value?: string;
}

export function SimpleDatePicker({ onDateChange, value }: SimpleDatePickerProps) {
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setSelectedDate(value || "");
    if (value) {
        const [day, month, year] = value.split("/");
        const newCurrentMonth = new Date(Number(year), Number(month) - 1, Number(day));
        if (!isNaN(newCurrentMonth.getTime())) {
            setCurrentMonth(newCurrentMonth);
        }
    }
  }, [value]);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const getMonthName = (month: number) => {
    return [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
    ][month];
  };

  const formatDateToBrazil = (date: string) => {
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleDateClick = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = (currentMonth.getMonth() + 1).toString().padStart(2, "0");
    const dayStr = day.toString().padStart(2, "0");
    
    const standardFormattedDate = `${year}-${month}-${dayStr}`;
    const brazilianFormattedDate = formatDateToBrazil(standardFormattedDate);

    setSelectedDate(brazilianFormattedDate);
    if (onDateChange) onDateChange(brazilianFormattedDate);
    
    onClose();
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const startDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  return (
    <Box w={'100%'}>
      <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
        <PopoverTrigger>
          <Input
            w={'100%'}
            value={selectedDate}
            placeholder="Selecione uma data"
            readOnly
            cursor="pointer"
            onClick={onOpen}
          />
        </PopoverTrigger>
        <PopoverContent w="fit-content" zIndex={9999}>
          <PopoverArrow />
          <PopoverBody>
            <Box textAlign="center" mb={2}>
              <Button size="sm" onClick={goToPreviousMonth} mr={2}>
                Anterior
              </Button>
              <Text display="inline" fontWeight="bold">
                {getMonthName(currentMonth.getMonth())} {currentMonth.getFullYear()}
              </Text>
              <Button size="sm" onClick={goToNextMonth} ml={2}>
                Próximo
              </Button>
            </Box>
            <Grid templateColumns="repeat(7, 1fr)" gap={1}>
              {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => (
                <Text key={day} fontSize="sm" textAlign="center">
                  {day}
                </Text>
              ))}
              {Array.from({ length: startDayOfMonth }).map((_, i) => (
                <Box key={`empty-${i}`}></Box>
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => (
                <Button
                  key={i + 1}
                  size="sm"
                  onClick={() => handleDateClick(i + 1)}
                  bg={
                    selectedDate.split("/")[0] === (i + 1).toString().padStart(2, '0') &&
                    currentMonth.getMonth() === (parseInt(selectedDate.split("/")[1], 10) - 1) &&
                    currentMonth.getFullYear() === parseInt(selectedDate.split("/")[2], 10)
                      ? "blue.300"
                      : "gray.100"
                  }
                  _hover={{ bg: "blue.200" }}
                >
                  {i + 1}
                </Button>
              ))}
            </Grid>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

export default SimpleDatePicker;